<template>
  <div class="login fill-height mt-1">
    <v-container fluid fill-height>
      <v-layout>
        <v-flex xs12 mt-5>
          <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit">
            <v-card class="elevation-0">
              <!-- <v-toolbar class="black--text font-weight-bold" color="white" flat>
              <v-toolbar-title>{{ $t("login.title") }}</v-toolbar-title>
            </v-toolbar> -->
              <div class="d-flex align-center flex-column mb-3">
                <h1 class="login-title">{{ $t("login.title") }}</h1>
                <h3 class="font-weight-regular login-sub">
                  {{ $t("login.subtitle") }}
                </h3>
              </div>
              <v-spacer></v-spacer>
              <v-card-text>
                <div class="login-form">
                  <v-text-field
                    v-if="!showMfa"
                    v-model="email"
                    class="email-input"
                    :rules="emailRules"
                    :error-messages="errors"
                    type="email"
                    label="Username"
                    autocomplete="off"
                    required
                    outlined
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-if="!showMfa"
                    v-model="password"
                    class="password-input"
                    label="Password"
                    autocomplete="off"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    :rules="passwordRules"
                    @click:append="toggleShowPassword"
                    required
                    outlined
                    dense
                  ></v-text-field>
                </div>

                <template v-if="showMfa">
                  <div class="mfa-section">
                    <div class="mb-2">
                      Il codice è stato inviato all’indirizzo email che hai
                      usato per registrarti: {{ email }}
                    </div>

                    <p style="margin-top: 15px;">
                      Controlla la casella di posta dell’email che hai inserito
                      come login quando hai creato il tuo account (inclusa la
                      cartella spam).
                    </p>
                    <div>
                      <v-otp-input
                        v-model="mfaPin"
                        :error-messages="errors"
                        :label="$t('login.mfaCode')"
                        dense
                        outlined
                        clearable
                      />
                    </div>
                  </div>
                </template>

                <div
                  class="d-flex align-center justify-space-between remember-password"
                >
                  <v-checkbox
                    color="primary"
                    class="ma-0"
                    hide-details
                    v-model="rememberMe"
                  >
                    <template v-slot:label>
                      <span>{{ $t("login.rememberMe") }}</span>
                    </template>
                  </v-checkbox>
                  <div class="recover-pass-wrap" v-if="enableRegistration">
                    <a
                      @click="goToResetPassword"
                      class="text-decoration-underline recover-password"
                    >
                      <strong>{{ $t("login.rememberPassword") }}</strong>
                    </a>
                  </div>

                  <div
                    class="recover-pass-wrap"
                    v-else-if="!$platform_is_cordova"
                  >
                    <a
                      href="https:/grosmarket.it/Recupera-Password"
                      class="text-decoration-underline recover-password"
                      >{{ $t("login.rememberPassword") }}</a
                    >
                  </div>
                </div>
                <RecaptchaDisclaimer></RecaptchaDisclaimer>

                <ResponseMessage :response="response" />
                <v-row no-gutters>
                  <v-spacer></v-spacer>
                  <v-col cols="12" sm="5">
                    <div class="mt-2" v-if="showMfa">
                      <v-btn
                        v-if="showResendOtp"
                        large
                        text
                        width="100%"
                        color="primary"
                        @click="resendMfaPinAndLogin"
                      >
                        Invia di nuovo il codice
                      </v-btn>
                      <span v-else>
                        Puoi richiedere un nuovo codice tra
                        {{ resendOtpCountdown }} secondi.
                      </span>
                    </div>
                    <v-btn
                      :loading="loading"
                      type="submit"
                      class="login-button"
                      large
                      width="100%"
                      depressed
                    >
                      {{ $t("login.buttonLogin") }}
                    </v-btn>
                  </v-col>
                  <v-spacer></v-spacer>
                </v-row>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <div
                  class="d-flex justify-content-center align-center mt-8 register-wrapper"
                  v-if="enableRegistration"
                  :class="{ 'flex-column': $vuetify.breakpoint.xs }"
                  width="100%"
                >
                  <div class="register-label">
                    <span>
                      {{ $t("login.messageRegister") }}
                    </span>
                  </div>
                  <v-btn
                    @click="handleRegister"
                    class="register-button"
                    large
                    outlined
                    width="100%"
                    depressed
                  >
                    {{ $t("login.buttonRegister") }}
                  </v-btn>
                </div>
                <div
                  class="d-flex flex-column align-center mt-2 register-wrapper"
                  v-else-if="!$platform_is_cordova"
                >
                  <div class="register-label">
                    {{ $t("login.messageRegister") }}
                  </div>
                  <v-btn
                    href="https://grosmarket.it/Registrazione"
                    class="register-button"
                    large
                    outlined
                    width="100%"
                    depressed
                    >{{ $t("login.buttonRegister") }}
                  </v-btn>
                </div>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import ResponseMessage from "@/components/ResponseMessage";
import RecaptchaDisclaimer from "@/commons/components/RecaptchaDisclaimer.vue";
import pushNotification from "~/mixins/pushNotification";

import form from "~/mixins/form";
import { mapActions, mapGetters } from "vuex";
import { requiredValue, isEmail } from "~/validator/validationRules";

export default {
  name: "Login",
  props: {
    hideRegistration: { type: Boolean, required: false, default: false }
  },
  mixins: [form, pushNotification],
  components: { ResponseMessage, RecaptchaDisclaimer },
  data() {
    return {
      loading: null,
      showPassword: false,
      email: "",
      password: "",
      mfaPin: "",
      showMfa: false,
      resendOtpTimer: null,
      resendOtpCountdown: 180, // seconds,
      //resendOtpCountdownDisplay: 3, // minutes
      resendMfaPin: false,
      rememberMe: true,
      response: {},
      valid: true,
      lazy: true,
      emailRules: [requiredValue("Digitare l'e-mail"), isEmail()],
      passwordRules: [requiredValue("Digitare la password")],
      errors: [],
      invalid: null,
      showResendOtp: false
    };
  },
  computed: {
    enableRegistration() {
      return global.config.registration;
    }
  },
  watch: {
    showMfa(newVal) {
      if (newVal) {
        this.startResendOtpTimer();
      } else {
        this.clearResendOtpTimer();
      }
    }
  },
  methods: {
    ...mapGetters({
      isAuthenticated: "cart/isAuthenticated"
    }),
    ...mapActions({
      doLogin: "cart/doLogin",
      loadCart: "cart/loadCart"
    }),
    goToResetPassword() {
      this.$router.push({
        name: "ResetPasswordRequest"
      });
      this.$emit("submit", false);
    },
    toggleShowPassword(e) {
      e.preventDefault();
      this.showPassword = !this.showPassword;
    },
    startResendOtpTimer() {
      this.showResendOtp = false;
      this.resendOtpCountdown = 180;
      if (this.resendOtpTimer) clearInterval(this.resendOtpTimer);
      this.resendOtpTimer = setInterval(() => {
        this.resendOtpCountdown--;
        if (this.resendOtpCountdown <= 0) {
          this.showResendOtp = true;
          clearInterval(this.resendOtpTimer);
          this.resendOtpTimer = null;
        }
      }, 1000);
    },
    clearResendOtpTimer() {
      if (this.resendOtpTimer) {
        clearInterval(this.resendOtpTimer);
        this.resendOtpTimer = null;
      }
      this.showResendOtp = false;
    },
    resendMfaPinAndLogin(e) {
      e.preventDefault();
      this.resendMfaPin = true;
      this.handleSubmit(e);
      this.resendMfaPin = false;
      this.showResendOtp = false;
      this.startResendOtpTimer();
    },
    async handleSubmit(e) {
      e.preventDefault();
      let token = null;
      let _this = this;
      if (
        _this.showMfa &&
        !_this.resendMfaPin &&
        (!_this.mfaPin || _this.mfaPin.length === 0 || _this.mfaPin.length < 6)
      ) {
        return;
      }
      if (_this.password.length > 0) {
        try {
          _this.loading = true;
          _this.response = {};
          await _this.doLogin({
            email: _this.email,
            password: _this.password,
            rememberMe: _this.rememberMe,
            token: token,
            mfaPin: _this.mfaPin,
            resendMfaPin: _this.resendMfaPin
          });
          // SE AUTENTICATO REGISTRA IL DEVICE PER PUSH NOTIFICATION
          // if (_this.isAuthenticated && global.config.pushNotificationEnabled) {
          //   console.log("IS AUTHENTICATED AND PUSH ENABLED");
          //   _this.registerFirebaseDevice();
          // }
          _this.$emit("submit", true);
        } catch (response) {
          _this.response = response;
          if (response.status == 4) {
            //need mfa
            _this.showMfa = true;
          }
        } finally {
          _this.loading = false;
        }
      }
    },
    handleRegister() {
      this.$router.push({
        name: "RegistrationUser",
        path: "/registration-user"
      });
      this.$emit("submit", false);
    }
  },
  beforeDestroy() {
    this.clearResendOtpTimer();
  }
};
</script>
